/**
 * BriefCreatorOverlay — single composer for every brief-creation door.
 *
 * State machine: client-persona → intent → input → committing → done.
 * Skip steps when caller pre-seeds via `defaultClientUid`/`defaultPersonaId`
 * (intent step is also skippable via `defaultIntent`).
 *
 * Right pane (DraftPreviewPanel) is always rendered during the input step
 * so the user sees the brief assemble in real time.  Commit posts to the
 * existing `POST /api/v1/generation/briefs` endpoint and the caller is
 * notified via `onBriefCreated(briefId)` to navigate.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import {
  generationApi,
  personasApiV2,
  type AdPreview,
  type BriefCreatorCommitPayload,
  type BriefDraft,
  type BriefIdea,
  type BriefTemplate,
  type GenerationBrief,
  type PersonaDetail,
} from '@/services/api';
import { ClientCombobox, type ClientOption } from '@/components/ClientCombobox';
import { IntentTiles } from './overlay/IntentTiles';
import { DraftPreviewPanel } from './overlay/DraftPreviewPanel';
import { SuggestedIdeasInput } from './overlay/SuggestedIdeasInput';
import { FreeformInput } from './overlay/FreeformInput';
import { SourceMaterialsInput } from './overlay/SourceMaterialsInput';
import { FromAnAdInput } from './overlay/FromAnAdInput';
import { TemplateInput } from './overlay/TemplateInput';
import {
  EMPTY_DRAFT_STATE,
  type DraftState,
  type OverlayIntent,
  type OverlayStep,
} from './overlay/types';

export interface BriefCreatorOverlayProps {
  open: boolean;
  onClose: () => void;
  onBriefCreated: (briefId: string) => void;

  defaultClientUid?: string;
  defaultPersonaId?: string;
  defaultIntent?: OverlayIntent;
  defaultAdId?: string;
  defaultClusterId?: string;
  defaultText?: string;
}

const INTENT_LABELS: Record<OverlayIntent, string> = {
  suggested: 'Ranked Ideas',
  freeform: 'Describe It',
  materials: 'Source Materials',
  from_ad: 'From an Ad',
  template: 'Template',
};

export function BriefCreatorOverlay({
  open,
  onClose,
  onBriefCreated,
  defaultClientUid,
  defaultPersonaId,
  defaultIntent,
  defaultAdId,
  defaultClusterId,
  defaultText,
}: BriefCreatorOverlayProps) {
  // ── state ────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<OverlayStep>(() =>
    initialStep(defaultClientUid, defaultPersonaId, defaultIntent),
  );
  const [clientUid, setClientUid] = useState(defaultClientUid ?? '');
  const [client, setClient] = useState<ClientOption | null>(null);
  const [personaId, setPersonaId] = useState(defaultPersonaId ?? '');
  const [personas, setPersonas] = useState<PersonaDetail[]>([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [draft, setDraft] = useState<DraftState>({
    ...EMPTY_DRAFT_STATE,
    intent: defaultIntent ?? null,
    freeformText: defaultText,
  });
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep(initialStep(defaultClientUid, defaultPersonaId, defaultIntent));
      setDraft({
        ...EMPTY_DRAFT_STATE,
        intent: defaultIntent ?? null,
        freeformText: defaultText,
      });
      setCommitError(null);
      setClientUid(defaultClientUid ?? '');
      setPersonaId(defaultPersonaId ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load personas when client is set and we haven't yet loaded
  useEffect(() => {
    if (!clientUid) {
      setPersonas([]);
      return;
    }
    let cancelled = false;
    setPersonasLoading(true);
    personasApiV2
      .listPersonas(clientUid)
      .then((res) => {
        if (!cancelled) setPersonas(res.personas ?? []);
      })
      .catch(() => {
        if (!cancelled) setPersonas([]);
      })
      .finally(() => {
        if (!cancelled) setPersonasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  const stepBack = () => {
    if (step === 'input') setStep('intent');
    else if (step === 'intent') setStep('client-persona');
  };

  const proceedFromClientPersona = () => {
    setStep('intent');
  };

  const selectIntent = (intent: OverlayIntent) => {
    setDraft((d) => ({ ...d, intent }));
    setStep('input');
  };

  // ── intent-specific handlers ─────────────────────────────────────────────

  const onIdeaSelected = (idea: BriefIdea) => {
    setDraft((d) => ({
      ...d,
      seedIdea: {
        idea_id: idea.idea_id,
        title: idea.title,
        premise: idea.premise,
        scenario_ids: idea.mapped_scenario_ids ?? [],
        persona_id: idea.chain_suggestion?.params?.persona_id,
      },
      name: idea.title,
      freeformText: idea.premise,
    }));
  };

  const onFreeformSynthesized = (brief: GenerationBrief) => {
    setDraft((d) => ({
      ...d,
      synthesizedBrief: {
        brief_id: brief.brief_id,
        name: brief.name,
        sections: extractSections(brief),
        persona_id: brief.persona_id,
      },
      name: brief.name,
    }));
  };

  const onFromAdLoaded = (adDraft: BriefDraft, preview: AdPreview) => {
    setDraft((d) => ({
      ...d,
      adDraft,
      adPreview: preview,
      name: adDraft.name,
    }));
  };

  const onTemplateSelected = (template: BriefTemplate) => {
    setDraft((d) => ({
      ...d,
      template,
      name: `${template.name} brief`,
    }));
  };

  // ── commit ───────────────────────────────────────────────────────────────

  const canCommit = useMemo(() => {
    if (!clientUid) return false;
    if (!draft.intent) return false;
    if (draft.intent === 'suggested') return !!draft.seedIdea;
    if (draft.intent === 'freeform' || draft.intent === 'materials')
      return !!draft.synthesizedBrief;
    if (draft.intent === 'from_ad') return !!draft.adDraft;
    if (draft.intent === 'template') return !!draft.template;
    return false;
  }, [clientUid, draft]);

  const handleCommit = async () => {
    if (!canCommit) return;
    setCommitting(true);
    setCommitError(null);
    setStep('committing');
    try {
      // Suggested + Freeform + Materials paths already produced a persisted
      // brief via the underlying skill / synthesize call.  Reuse it.
      if (draft.synthesizedBrief?.brief_id) {
        onBriefCreated(draft.synthesizedBrief.brief_id);
        return;
      }

      if (draft.intent === 'suggested' && draft.seedIdea) {
        // Use synthesize to persist a brief seeded by the idea premise.
        const brief = await generationApi.synthesizeBrief({
          client_uid: clientUid,
          prompt: draft.seedIdea.premise,
          brief_name: draft.seedIdea.title,
        });
        onBriefCreated(brief.brief_id);
        return;
      }

      if (draft.intent === 'from_ad' && draft.adDraft) {
        const payload = buildFromAdCommitPayload(draft.adDraft, clientUid, personaId);
        const brief = await generationApi.createBrief(payload);
        onBriefCreated(brief.brief_id);
        return;
      }

      if (draft.intent === 'template' && draft.template) {
        const payload = buildTemplateCommitPayload(
          draft.template,
          clientUid,
          personaId,
        );
        const brief = await generationApi.createBrief(payload);
        onBriefCreated(brief.brief_id);
        return;
      }

      throw new Error('No draft data available to commit.');
    } catch (err) {
      setCommitError((err as Error).message || 'Brief commit failed');
      setStep('input');
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-[92%] max-w-7xl h-[88vh] flex flex-col overflow-hidden">
        <Header
          step={step}
          intent={draft.intent}
          clientName={client?.name || clientUid}
          personaId={personaId}
          onClose={onClose}
          onBack={stepBack}
          canBack={step === 'intent' || step === 'input'}
          defaultClusterId={defaultClusterId}
        />

        <div className="flex-1 min-h-0 flex">
          {step === 'client-persona' ? (
            <ClientPersonaStep
              clientUid={clientUid}
              client={client}
              personaId={personaId}
              personas={personas}
              personasLoading={personasLoading}
              onClientChange={(uid, opt) => {
                setClientUid(uid);
                setClient(opt ?? null);
                setPersonaId('');
              }}
              onPersonaChange={setPersonaId}
              onContinue={proceedFromClientPersona}
            />
          ) : step === 'intent' ? (
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">
                How would you like to start?
              </h2>
              <p className="text-[12px] text-slate-500 mb-6">
                Pick the path that matches the inputs you have on hand.
              </p>
              <IntentTiles
                hasPersona={!!personaId}
                onSelect={selectIntent}
              />
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,_1fr)_420px] min-h-0">
              <div className="overflow-y-auto px-8 py-6">
                <InputBody
                  intent={draft.intent}
                  clientUid={clientUid}
                  personaId={personaId}
                  defaultText={draft.freeformText ?? defaultText}
                  defaultAdId={defaultAdId}
                  selectedTemplateId={draft.template?.template_id}
                  onIdeaSelected={onIdeaSelected}
                  onFreeformSynthesized={onFreeformSynthesized}
                  onMaterialsSynthesized={onFreeformSynthesized}
                  onFromAdLoaded={onFromAdLoaded}
                  onTemplateSelected={onTemplateSelected}
                />
              </div>
              <DraftPreviewPanel state={draft} />
            </div>
          )}
        </div>

        {step !== 'client-persona' && (
          <Footer
            canCommit={canCommit}
            committing={committing || step === 'committing'}
            commitError={commitError}
            onCommit={handleCommit}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Header / footer / step bodies
// ────────────────────────────────────────────────────────────────────────────

function Header({
  step,
  intent,
  clientName,
  personaId,
  onClose,
  onBack,
  canBack,
  defaultClusterId,
}: {
  step: OverlayStep;
  intent: OverlayIntent | null;
  clientName: string;
  personaId: string;
  onClose: () => void;
  onBack: () => void;
  canBack: boolean;
  defaultClusterId?: string;
}) {
  const stepLabel: Record<OverlayStep, string> = {
    'client-persona': 'Who is this brief for?',
    intent: 'Choose a starting point',
    input: intent ? INTENT_LABELS[intent] : 'Compose',
    preview: 'Preview',
    committing: 'Saving brief…',
  };
  return (
    <header className="flex items-center justify-between gap-3 px-6 py-3 border-b border-slate-200 bg-slate-50/40">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {canBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-slate-500 hover:text-slate-800 transition"
            title="Back"
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            New Brief
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate">
            {stepLabel[step]}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4 flex-wrap">
          {clientName && (
            <Pill tone="slate" label={clientName} />
          )}
          {personaId && <Pill tone="violet" label={personaId} />}
          {defaultClusterId && (
            <Pill tone="fuchsia" label="cluster bias" />
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-slate-400 hover:text-slate-800 transition"
        title="Close"
      >
        <X size={18} />
      </button>
    </header>
  );
}

function Pill({
  label,
  tone,
}: {
  label: string;
  tone: 'slate' | 'violet' | 'fuchsia';
}) {
  const cls =
    tone === 'slate'
      ? 'bg-slate-100 text-slate-600 border-slate-200'
      : tone === 'violet'
        ? 'bg-violet-50 text-violet-700 border-violet-200'
        : 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200';
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}
    >
      {label}
    </span>
  );
}

function Footer({
  canCommit,
  committing,
  commitError,
  onCommit,
  onClose,
}: {
  canCommit: boolean;
  committing: boolean;
  commitError: string | null;
  onCommit: () => void;
  onClose: () => void;
}) {
  return (
    <footer className="flex items-center gap-3 justify-between px-6 py-3 border-t border-slate-200 bg-white">
      <div className="min-h-[18px] text-[12px] text-rose-600">
        {commitError}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={committing}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onCommit}
          disabled={!canCommit || committing}
          className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
        >
          {committing && <Loader2 size={12} className="animate-spin" />}
          Create brief →
        </button>
      </div>
    </footer>
  );
}

function ClientPersonaStep({
  clientUid,
  client,
  personaId,
  personas,
  personasLoading,
  onClientChange,
  onPersonaChange,
  onContinue,
}: {
  clientUid: string;
  client: ClientOption | null;
  personaId: string;
  personas: PersonaDetail[];
  personasLoading: boolean;
  onClientChange: (uid: string, opt: ClientOption | null) => void;
  onPersonaChange: (id: string) => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto px-8 py-8">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Who is this brief for?
          </h2>
          <p className="text-[12px] text-slate-500 mt-1">
            Pick the client and (optionally) the target persona — you can change
            either later.
          </p>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Client
          </label>
          <div className="mt-1">
            <ClientCombobox
              value={clientUid}
              onChange={(uid, opt) => onClientChange(uid, opt ?? null)}
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Persona <span className="text-slate-400 normal-case">(optional)</span>
          </label>
          <select
            value={personaId}
            onChange={(e) => onPersonaChange(e.target.value)}
            disabled={!clientUid || personasLoading}
            className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-slate-200 disabled:bg-slate-50"
          >
            <option value="">— No persona —</option>
            {personas.map((p) => (
              <option key={p.id} value={p.slug || p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {personasLoading && (
            <div className="text-[11px] text-slate-400 mt-1">Loading personas…</div>
          )}
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={onContinue}
            disabled={!clientUid}
            className="px-4 py-2 text-xs font-semibold text-white bg-brand-purple rounded-lg hover:bg-brand-purple/90 disabled:opacity-50"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}

function InputBody({
  intent,
  clientUid,
  personaId,
  defaultText,
  defaultAdId,
  selectedTemplateId,
  onIdeaSelected,
  onFreeformSynthesized,
  onMaterialsSynthesized,
  onFromAdLoaded,
  onTemplateSelected,
}: {
  intent: OverlayIntent | null;
  clientUid: string;
  personaId: string;
  defaultText?: string;
  defaultAdId?: string;
  selectedTemplateId?: string;
  onIdeaSelected: (idea: BriefIdea) => void;
  onFreeformSynthesized: (brief: GenerationBrief) => void;
  onMaterialsSynthesized: (brief: GenerationBrief) => void;
  onFromAdLoaded: (draft: BriefDraft, preview: AdPreview) => void;
  onTemplateSelected: (template: BriefTemplate) => void;
}) {
  if (intent === 'suggested') {
    if (!personaId) {
      return (
        <div className="text-[12px] text-slate-500 py-12 text-center">
          Suggested ideas need a persona.  Go back and pick one.
        </div>
      );
    }
    return (
      <SuggestedIdeasInput
        clientUid={clientUid}
        personaId={personaId}
        onSelect={onIdeaSelected}
      />
    );
  }
  if (intent === 'freeform') {
    return (
      <FreeformInput
        clientUid={clientUid}
        personaId={personaId || undefined}
        defaultText={defaultText}
        onSynthesized={onFreeformSynthesized}
      />
    );
  }
  if (intent === 'materials') {
    return (
      <SourceMaterialsInput
        clientUid={clientUid}
        personaId={personaId || undefined}
        onSynthesized={onMaterialsSynthesized}
      />
    );
  }
  if (intent === 'from_ad') {
    return (
      <FromAnAdInput
        clientUid={clientUid}
        personaId={personaId || undefined}
        defaultAdId={defaultAdId}
        onLoaded={onFromAdLoaded}
      />
    );
  }
  if (intent === 'template') {
    return (
      <TemplateInput
        selectedTemplateId={selectedTemplateId}
        onSelect={onTemplateSelected}
      />
    );
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function initialStep(
  defaultClientUid: string | undefined,
  defaultPersonaId: string | undefined,
  defaultIntent: OverlayIntent | undefined,
): OverlayStep {
  if (!defaultClientUid) return 'client-persona';
  if (defaultIntent) return 'input';
  if (!defaultPersonaId) {
    // Have client but no persona — let user pick whether to add persona before
    // intent.  We still show the persona step (with persona optional).
    return 'client-persona';
  }
  return 'intent';
}

function extractSections(
  brief: GenerationBrief,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  const keys = [
    'objective',
    'audience',
    'insight',
    'brand_voice',
    'message',
    'creative_context',
    'mandatories',
    'success_metrics',
  ] as const;
  for (const k of keys) {
    const v = (brief as unknown as Record<string, unknown>)[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = v as Record<string, unknown>;
    }
  }
  return out;
}

function buildFromAdCommitPayload(
  adDraft: BriefDraft,
  clientUid: string,
  personaId: string,
): BriefCreatorCommitPayload {
  const sectionContent = (key: string) =>
    (adDraft.sections[key]?.content ?? {}) as Record<string, unknown>;
  return {
    name: adDraft.name,
    client_uid: clientUid,
    description: 'Back-filled from existing ad',
    persona_id: personaId || adDraft.inferred_persona_id || undefined,
    source_type: 'asset_backfill',
    objective: sectionContent('objective'),
    audience: sectionContent('audience'),
    insight: sectionContent('insight'),
    brand_voice: sectionContent('brand_voice'),
    message: sectionContent('message'),
    creative_context: sectionContent('creative_context'),
    mandatories: sectionContent('mandatories'),
    success_metrics: sectionContent('success_metrics'),
    metadata: {
      source_asset_id: adDraft.source_asset_id,
      inferred_from_signals: adDraft.inferred_from_signals,
    },
  };
}

function buildTemplateCommitPayload(
  template: BriefTemplate,
  clientUid: string,
  personaId: string,
): BriefCreatorCommitPayload {
  const sectionContent = (key: string) =>
    (template.prefill_sections[key] ?? {}) as Record<string, unknown>;
  return {
    name: `${template.name} brief`,
    client_uid: clientUid,
    description: `Created from template: ${template.name}`,
    persona_id: personaId || undefined,
    source_type: 'template_clone',
    template_id: template.template_id,
    objective: sectionContent('objective'),
    audience: sectionContent('audience'),
    insight: sectionContent('insight'),
    brand_voice: sectionContent('brand_voice'),
    message: sectionContent('message'),
    creative_context: sectionContent('creative_context'),
    mandatories: sectionContent('mandatories'),
    success_metrics: sectionContent('success_metrics'),
    channel_directives: template.default_channel_directives ?? [],
    metadata: { template_id: template.template_id },
  };
}
